import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import {
  materialTags,
  tacticTags,
} from './libraryData';
import { useMaterials, useOrgs } from '../../hooks/useLibraryData';
import './Library.css';

// Filter toggle button
function FilterButton({ label, count, isOpen, onClick }) {
  const hasSelection = count > 0;
  return (
    <button
      className={`hf-dropdown-btn ${hasSelection ? 'has-selection' : ''} ${isOpen ? 'is-open' : ''}`}
      onClick={onClick}
    >
      {label} {count > 0 && `(${count})`} <span className="hf-arrow"></span>
    </button>
  );
}

// Inline expand panel
function ExpandPanel({ options, selected, onToggle }) {
  return (
    <div className="hf-expand-panel">
      {options.map(opt => (
        <span
          key={opt}
          className={`hf-panel-option ${selected.includes(opt) ? 'selected' : ''}`}
          onClick={() => onToggle(opt)}
        >
          {opt}
        </span>
      ))}
    </div>
  );
}

// Highlight search terms in text
function highlightText(text, query) {
  if (!text || !query.trim()) return text;
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return text;
  const escaped = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="search-highlight">{part}</mark>
      : part
  );
}

export default function MaterialsPage() {
  const { materials: materialsData } = useMaterials();
  const { orgs: orgsData } = useOrgs();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeContentTags, setActiveContentTags] = useState([]);
  const [activeTacticTags, setActiveTacticTags] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  const togglePanel = (name) => setOpenPanel(prev => prev === name ? null : name);

  const getOrgById = useCallback((id) => orgsData.find(org => org.id === id), [orgsData]);

  // Toggle helpers
  const toggleContentTag = (tag) => setActiveContentTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  const toggleTacticTag = (tag) => setActiveTacticTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  // Filter materials
  const filteredMaterials = useMemo(() => {
    let results = [...materialsData];
    if (activeContentTags.length > 0) {
      results = results.filter(m => m.tags && m.tags.some(tag => activeContentTags.includes(tag)));
    }
    if (activeTacticTags.length > 0) {
      results = results.filter(m => m.tactics && m.tactics.some(tag => activeTacticTags.includes(tag)));
    }
    const matWords = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (matWords.length > 0) {
      results = results.filter(m => {
        const haystack = `${(m.title || '')} ${(m.description || '')} ${(m.source || '')} ${(m.tags || []).join(' ')} ${(m.tactics || []).join(' ')}`.toLowerCase();
        return matWords.every(w => haystack.includes(w));
      });
    }
    // When searching, sort by relevance; otherwise by date
    if (matWords.length > 0) {
      results.sort((a, b) => {
        const aTitle = (a.title || '').toLowerCase();
        const bTitle = (b.title || '').toLowerCase();
        let aScore = 0, bScore = 0;
        for (const w of matWords) {
          if (aTitle.includes(w)) aScore += 100;
          if ((a.source || '').toLowerCase().includes(w)) aScore += 20;
          if ((a.description || '').toLowerCase().includes(w)) aScore += 5;
          if (bTitle.includes(w)) bScore += 100;
          if ((b.source || '').toLowerCase().includes(w)) bScore += 20;
          if ((b.description || '').toLowerCase().includes(w)) bScore += 5;
        }
        if (bScore !== aScore) return bScore - aScore;
        return new Date(b.date || 0) - new Date(a.date || 0);
      });
    } else {
      results.sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(b.date) - new Date(a.date);
      });
    }
    return results;
  }, [activeContentTags, activeTacticTags, searchQuery, materialsData]);

  // Stats
  const totalMaterials = materialsData.length;
  const hasFilters = activeContentTags.length > 0 || activeTacticTags.length > 0 || searchQuery.trim() !== '';

  const handleClearAll = () => {
    setActiveContentTags([]);
    setActiveTacticTags([]);
    setSearchQuery('');
  };

  return (
    <>
      <Helmet>
        <title>Materials - Hasbara Tracker</title>
        <meta name='description' content='Browse documented Israeli propaganda materials, including reports, talking points, training guides, and media kits' />
      </Helmet>

      <Header />

      <main className='library-page'>
        {/* Page Header */}
        <section className='library-header'>
          <h1 className='library-page-title'>Materials</h1>
          <p className='library-intro'>
            A living archive of propaganda materials produced by Israeli state and pro-Israel advocacy organisations. Documents are sourced, archived, and annotated for public accountability.
          </p>
          <p className='library-stats-line'>
            {totalMaterials} materials documented
          </p>
        </section>

        {/* Search bar */}
        <input
          type='text'
          placeholder='Search materials...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className='hf-search'
        />

        {/* Filter bar */}
        <div className='hf-filter-bar'>
          <div className='hf-button-row'>
            <FilterButton label='Type' count={activeContentTags.length} isOpen={openPanel === 'type'} onClick={() => togglePanel('type')} />
            <FilterButton label='Tactic' count={activeTacticTags.length} isOpen={openPanel === 'tactic'} onClick={() => togglePanel('tactic')} />
            {hasFilters && <button className='hf-clear-all' onClick={handleClearAll}>Clear all</button>}
          </div>
          {openPanel === 'type' && <ExpandPanel options={materialTags} selected={activeContentTags} onToggle={toggleContentTag} />}
          {openPanel === 'tactic' && <ExpandPanel options={tacticTags} selected={activeTacticTags} onToggle={toggleTacticTag} />}
        </div>

        <p className='library-results-count'>
          {filteredMaterials.length} material{filteredMaterials.length !== 1 ? 's' : ''}
          {hasFilters ? ` (filtered from ${totalMaterials})` : ''}
        </p>

        {/* Materials List */}
        <div className='library-items-list'>
          {filteredMaterials.length > 0 ? (
            filteredMaterials.map(material => (
              <Link key={material.slug} to={`/materials/${material.slug}`} className='library-material-item'>
                <div className='library-type-badge'>{material.type.toUpperCase()}</div>
                <div className='library-item-content'>
                  <div className='library-item-header'>
                    <h3 className='library-item-title'>{highlightText(material.title, searchQuery)}</h3>
                    {material.date && (
                      <span className='library-item-date'>
                        {new Date(material.date).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </div>
                  {material.description && <p className='library-item-description'>{highlightText(material.description, searchQuery)}</p>}
                  <div className='library-item-footer'>
                    <div className='library-item-tags'>
                      {material.tags && material.tags.map(tag => <span key={tag} className='library-tag content-tag'>{tag}</span>)}
                      {material.tactics && material.tactics.map(tag => <span key={tag} className='library-tag tactic-tag'>{tag}</span>)}
                    </div>
                    {material.orgId && getOrgById(material.orgId) && (
                      <Link to={`/organisations/${getOrgById(material.orgId).slug}`} className='library-org-link' onClick={(e) => e.stopPropagation()}>
                        {getOrgById(material.orgId).name}
                      </Link>
                    )}
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className='library-no-results'><p>No materials found matching your filters.</p></div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
